import { useCallback } from "react";
import { CellValueChangedEvent } from "ag-grid-community";

import { showWarning } from "src/utils/toast/toastUtil";
import {
	getInspectionValueValidationMessage,
	isValidInspectionValue,
	normalizeInspectionValue,
} from "src/utils/inspection/inspectionValueUtils";

import {
	TQC3010_R01DetailDto,
	TQC3010_R01_VALUE_FIELDS,
} from "../Dto_TQC3010_R01";

const VALUE_FIELDS: (keyof TQC3010_R01DetailDto)[] = [...TQC3010_R01_VALUE_FIELDS];
const EDITABLE_FIELDS: (keyof TQC3010_R01DetailDto)[] = [...VALUE_FIELDS, "remark"];

const validateInspectValue = (row: TQC3010_R01DetailDto, inputValue: string) => {
	if (inputValue === "") {
		return { isValid: true, message: "" };
	}
	return {
		isValid: isValidInspectionValue(row.valueType ?? "", inputValue),
		message: getInspectionValueValidationMessage(row.valueType ?? ""),
	};
};

interface Props {
	updateTQC3010_R01DetailList: (valueIndex: number, data: TQC3010_R01DetailDto) => void;
	originalTQC3010_R01DetailList: TQC3010_R01DetailDto[];
}

export function useTQC3010_R01DetailCellChange({
	updateTQC3010_R01DetailList,
	originalTQC3010_R01DetailList,
}: Props) {
	const handleCellChange = useCallback(
		(event: CellValueChangedEvent<TQC3010_R01DetailDto>) => {
			const { data, colDef, newValue } = event;
			if (!data || !colDef.field) return;
			const field = colDef.field as keyof TQC3010_R01DetailDto;
			const isValueField = VALUE_FIELDS.includes(field);
			const inputValue = normalizeInspectionValue(data.valueType ?? "", newValue);
			const validation = isValueField
				? validateInspectValue(data, inputValue)
				: { isValid: true, message: "" };
			const updated = {
				...data,
				[field]: isValueField
					? validation.isValid
						? inputValue
						: ""
					: newValue,
				isModified: true,
			} as TQC3010_R01DetailDto;
			const original = originalTQC3010_R01DetailList.find(
				(item) => item.valueIndex === data.valueIndex,
			);
			if (original) {
				updated.isModified = !EDITABLE_FIELDS.every(
					(key) => (original[key] ?? "") === (updated[key] ?? ""),
				);
			}
			updateTQC3010_R01DetailList(data.valueIndex, updated);
			if (!validation.isValid && event.node.rowIndex !== null) {
				const rowIndex = event.node.rowIndex;
				showWarning(validation.message);
				setTimeout(() => {
					event.api.ensureIndexVisible(rowIndex);
					event.api.setFocusedCell(rowIndex, colDef.field!);
					event.api.startEditingCell({ rowIndex, colKey: colDef.field! });
				}, 0);
			}
		},
		[originalTQC3010_R01DetailList, updateTQC3010_R01DetailList],
	);
	return { handleCellChange };
}
